import CircularFilledIcon from "@/components/CircularButton/styled/CircularFilledIcon";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { Editor, EditorContent, useEditor } from "@knowt/editor/react";
import { ArrowUp } from "lucide-react";
import { extensionKit } from "@/components/FullPageEditor/extensions";
import toast from "react-hot-toast";
import { S3UploadJobContextProvider } from "@knowt/syncing/context/S3UploadJobContext";
import {
    RichEditorAttachmentList,
    RichEditorResponseToolbar,
} from "@/components/RichResponseEditor/RichResponseEditor";
import { LocalMessageAttachment } from "@knowt/syncing/types/common";
import useCombinedState from "@knowt/syncing/utils/hooks/useCombinedState";
import LazyLoaded from "@/components/LazyLoaded";
import dynamic from "next/dynamic";
import { editorInsertMathExpression } from "@/components/FullPageEditor/utils/mathematics";
import { useEffect, useState } from "react";
import React from "react";
import Mountable from "@/components/styled/div/Mountable";
import "@/components/FullPageEditor/styles/richEditor.css";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";

const MathFieldInputPopup = dynamic(() => import("@/components/MathField/MathFieldInputPopup"), { ssr: false });

type ChatInputProps = {
    id?: string;
    placeholder?: string;
    inputContainerRef?: React.RefObject<HTMLDivElement | null>;
    disabled?: boolean;
    disabledReason?: string;
    onSubmit: (value?: string) => Promise<void> | void;
    onChange?: (content: string | null) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    chosenSuggestion?: string | null;
    submitText?: string;
    styles?: {
        attachmentTagStyle?: React.CSSProperties;
        buttonStyle?: React.CSSProperties;
        buttonsContainerStyle?: React.CSSProperties;
        inputStyle?: React.CSSProperties;
        containerStyle?: React.CSSProperties;
        attachmentListStyle?: React.CSSProperties;
    };
    disableUpload?: boolean;
} & (
    | {
          uploadDestination: string;
          attachments: LocalMessageAttachment[];
          onUpdateAttachments: (attachments: LocalMessageAttachment[]) => void;
      }
    | {
          uploadDestination?: never;
          attachments?: never;
          onUpdateAttachments?: never;
      }
);

const ChatInput = React.forwardRef<HTMLInputElement, ChatInputProps>(
    (
        {
            id,
            placeholder,
            inputContainerRef,
            disabled: _disabled,
            disabledReason,
            disableUpload,
            onSubmit,
            onChange,
            uploadDestination,
            onKeyDown,
            chosenSuggestion,
            attachments,
            onUpdateAttachments,
            styles,
            submitText,
        },
        ref
    ) => {
        const [value, setValue] = useState("");
        const [isSendButtonHidden, setIsSendButtonHidden] = useState(false);
        const disabled = _disabled || (!value && !attachments?.length);
        const inputContainerHeight = inputContainerRef?.current?.clientHeight || 0;
        const heightWhenInputHasMoreThanTwoLines = 50;
        const hasInputSurpassedTwoLines = inputContainerHeight > heightWhenInputHasMoreThanTwoLines;

        const [editorMathPopupStates, setEditorMathPopupStates] = useCombinedState<{
            isOpen: boolean;
            content: string;
            editor: Editor | null;
            editorPos: { start: number; end: number };
        }>({ isOpen: false, content: "", editor: null, editorPos: { start: 0, end: 0 } });

        const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && !e.shiftKey && !editor?.isActive("codeBlock")) {
                e.preventDefault();
                editor?.commands.clearContent();
                await onSubmit(value);
            }
            onKeyDown?.(e);
        };

        const handleChat = async () => {
            if (!value && !attachments?.length) return toast.error("Please enter a message or attach a file");
            if (disabled) return toast.error(disabledReason || "Chat is disabled");
            await onSubmit(value);
            editor?.commands.clearContent();
        };

        const handleChange = (content: string | null) => {
            setValue(content || "");
            onChange?.(content);
        };

        const editor = useEditor({
            content: value,
            editable: true,
            immediatelyRender: false,
            extensions: extensionKit({
                editable: true,
                placeholderOptions: { placeholder: placeholder || "" },
                copilotProps: {
                    disabled: true,
                },
                onKatexClick: (_e, { content, pos, editor }) => {
                    if (pos.start && pos.end) {
                        setEditorMathPopupStates({ isOpen: true, editorPos: pos, content, editor });
                    }
                },
            }),
            onUpdate: ({ editor }) => {
                const content = editor.getText() ? editor.getHTML() : "";
                handleChange(content);
            },
        });

        // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
        useEffect(() => {
            if (chosenSuggestion) {
                editor?.commands.setContent(chosenSuggestion);
            }
        }, [chosenSuggestion]);

        return (
            <S3UploadJobContextProvider bucketName={uploadDestination} isDisabled={!uploadDestination || disableUpload}>
                <FlexColumn
                    style={{
                        height: "fit-content",
                        gap: spacing.XS,
                        ...styles?.containerStyle,
                    }}>
                    <Mountable mount={!!uploadDestination}>
                        <RichEditorAttachmentList
                            localEditorAttachments={attachments}
                            handleUpdateAttachments={onUpdateAttachments}
                            style={styles?.attachmentListStyle}
                            attachmentTagStyle={styles?.attachmentTagStyle}
                        />
                    </Mountable>
                    <EditorContent
                        id={id}
                        ref={ref}
                        editor={editor}
                        onKeyDown={handleKeyDown}
                        style={{
                            flex: 1,
                            outline: "none",
                            ...styles?.inputStyle,
                        }}
                    />
                    <FlexRowAlignCenter
                        style={{
                            alignSelf: hasInputSurpassedTwoLines ? "flex-end" : "center",
                            gap: spacing.XS,
                            ...styles?.buttonsContainerStyle,
                        }}>
                        <FlexRowAlignCenter
                            style={{
                                gap: spacing.XS,
                            }}>
                            <RichEditorResponseToolbar
                                editor={editor}
                                localEditorAttachments={attachments || null}
                                handleUpdateAttachments={onUpdateAttachments}
                                buttonStyle={styles?.buttonStyle}
                                stretchToolbar={setIsSendButtonHidden}
                            />
                        </FlexRowAlignCenter>

                        {!isSendButtonHidden &&
                            (submitText ? (
                                <CircularRectTextButton
                                    className="bodyBold2"
                                    onClick={handleChat}
                                    style={{
                                        width: "9rem",
                                        height: "4rem",
                                        background: themeColors.neutralBlack,
                                        color: themeColors.neutralWhite,
                                        opacity: disabled ? 0.5 : 1,
                                    }}>
                                    {submitText}
                                </CircularRectTextButton>
                            ) : (
                                <CircularFilledIcon
                                    Icon={ArrowUp}
                                    sx={{
                                        marginLeft: "1rem",
                                        opacity: disabled ? 0.5 : 1,
                                    }}
                                    radius="3rem"
                                />
                            ))}
                    </FlexRowAlignCenter>
                    <LazyLoaded load={editorMathPopupStates.isOpen}>
                        <MathFieldInputPopup
                            isOpen={editorMathPopupStates.isOpen}
                            onClose={() =>
                                setEditorMathPopupStates({
                                    isOpen: false,
                                    content: "",
                                    editor: null,
                                    editorPos: { start: 0, end: 0 },
                                })
                            }
                            onInsert={value => {
                                const { editor, editorPos } = editorMathPopupStates;
                                if (!editor || !editorPos) return;
                                editorInsertMathExpression({ editor, pos: editorPos, expression: value });
                            }}
                            selectedLatex={editorMathPopupStates.content}
                        />
                    </LazyLoaded>
                </FlexColumn>
            </S3UploadJobContextProvider>
        );
    }
);

export default ChatInput;
